/**
 * AdminForgotPassword — ADR-0022 admin scope.
 *
 * Strona „Zapomnialem hasla" dla panelu administracyjnego ITE staff.
 * Pole identifier (email lub login) — backend auto-detect po obecnosci '@'.
 *
 * Anti-enumeration: backend zawsze zwraca 200 z generic message niezaleznie
 * czy admin istnieje. UI po kazdej odpowiedzi pokazuje ekran "Sprawdz skrzynke".
 *
 * 429 nigdy nie dochodzi do FE — backend swallow'uje rate-limit do 200 (anti-enum).
 * Inne errory (5xx, network) → toast.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'sent'; identifier: string };

export function AdminForgotPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { admin, loading } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    if (!loading && admin !== null) {
      navigate('/', { replace: true });
    }
  }, [loading, admin, navigate]);

  if (loading || admin !== null) return null;

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: 'submitting' });
    try {
      await api.post('/admin/forgot-password', { identifier });
      setState({ kind: 'sent', identifier });
    } catch {
      toast.show({
        variant: 'error',
        title: t('auth.adminForgot.errors.genericTitle'),
        description: t('auth.adminForgot.errors.genericDescription'),
      });
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'sent') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="admin-forgot-password-sent"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">
              {t('auth.adminForgot.sentTitle')}
            </h1>
            <p className="mt-3 text-sm text-text-tertiary">
              {t('auth.adminForgot.sentDescription', { identifier: state.identifier })}
            </p>
          </div>
          <div className="mt-8">
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.adminForgot.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.adminForgot.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.adminForgot.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.adminForgot.identifierLabel')}
            type="text"
            autoComplete="username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={t('auth.adminForgot.identifierPlaceholder')}
            disabled={submitting}
            data-testid="admin-forgot-password-identifier"
          />

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="admin-forgot-password-submit"
          >
            {submitting ? t('auth.adminForgot.submitting') : t('auth.adminForgot.submit')}
          </Button>

          <div className="flex items-center justify-center pt-2 text-xs">
            <Link
              to="/login"
              className="text-text-tertiary underline underline-offset-2 hover:text-text-primary"
            >
              {t('auth.adminForgot.backToLogin')}
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
